import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Product } from 'src/@omnial/_models/catalog/product.model';
import { MenuService } from 'src/@omnial/_services/navigation/menu.service';
import { AppSettings } from 'src/app/app.settings';
import { environment } from 'src/environments/environment';
import { ProductsPaged } from 'src/@omnial/_models/catalog/products.paged.model';
import { PageState } from 'src/@omnial/_models/catalog/pageState.model';
import { CatalogSortOption } from 'src/@omnial/_models/settings.model';
import { ProductFilterGroup, ProductFilter, ProductFilterOption } from 'src/@omnial/_models/catalog/product-filter.model';
import { ProductsPagedService } from 'src/@omnial/_services/catalog/products-paged.service';
import { Menu } from 'src/@omnial/_models/navigation/menu.model';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-elfa-product-filters',
    templateUrl: './elfa-product-filters.component.html',
    styleUrls: ['./elfa-product-filters.component.scss'],
    standalone: false
})
export class ElfaProductFiltersComponent implements OnDestroy {
  @Input() elfaFilterType: string;
  @Output() searchFilters = new EventEmitter<ProductFilterOption[]>();
  public seName: string;
  public unFilteredProducts: Product[];
  public filterGroups: ProductFilterGroup[];
  public filterType = 'filter';
  public filterTab: number;
  public hasFilters: boolean;
  public noFilters: number;
  public noFiltered: number;
  public filterInfoTimer: NodeJS.Timeout;
  public filtering = false;
  public filtered = false;
  public filtersClosed: boolean;
  public selectedSort: CatalogSortOption;
  public sortOptions: CatalogSortOption[];
  public menu: Menu;
  public subscriptions: Subscription[] = [];

  constructor(
    public productsPagedService: ProductsPagedService,
    public menuService: MenuService,
    public settings: AppSettings,
    public spinner: NgxSpinnerService) {
    this.subscriptions.push(this.menuService.megaMenu.subscribe(res => { this.menu = res as Menu; }));
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => { sub.unsubscribe(); });
    }
  }

  public setFilters(seName: string, products: Product[], filterGroups: ProductFilterGroup[], pageState?: PageState) {
    this.seName = seName;
    this.unFilteredProducts = products;
    this.filterGroups = filterGroups;
    this.filterTab = 0;
    this.hasFilters = false;
    this.noFilters = 0;
    this.noFiltered = 0;
    this.filtersClosed = true;

    // We have the initial products and Product Spec Filters ... but we only want to show the applicable filters
    let validFilters: ProductFilterGroup[] = [];
    let filterUpFront = false;
    if (this.settings.maintainSort && pageState?.sort) {
      this.selectedSort = pageState?.sort;
    } else if (this.sortOptions?.length > 0) {
      this.selectedSort = this.sortOptions[0];
    }
    filterGroups?.forEach((group) => {
      let validFilterGroup: ProductFilterGroup = null;
      group.filters.forEach((filter) => {
        let validFilter: ProductFilter = null;
        filter.options.forEach((option) => {
          if (option.specificationAttributeName === 'SubCategory') {
            option.disabled = true;
          }
          if (option.specificationAttributeName === 'Brand') {
            if (!option.imageUrl) {
              option.imageUrl = this.BrandImage(option.name);
            }
          }
          if (this.settings.maintainFilters && pageState?.selectedFilters?.length > 0) {
            option.checked = pageState.selectedFilters.some(o => o.specificationAttributeId === option.specificationAttributeId && o.name === option.name);
            filterUpFront = true;
          }
          if (this.unFilteredProducts?.some(p => p.specifications?.some(spec => spec.specificationAttributeId === option.specificationAttributeId && spec.valueRaw === option.name))) {
            this.hasFilters = true;
            if (!validFilterGroup) {
              validFilterGroup = new ProductFilterGroup();
              validFilterGroup.name = group.name;
              validFilterGroup.displayOrder = group.displayOrder;
              validFilterGroup.filters = [];
              validFilter = new ProductFilter();
              validFilter.name = filter.name;
              validFilter.displayOrder = filter.displayOrder;
              validFilter.options = [option];
              validFilterGroup.filters = [validFilter];
              validFilters.push(validFilterGroup);
            } else if (!validFilter) {
              validFilter = new ProductFilter();
              validFilter.name = filter.name;
              validFilter.displayOrder = filter.displayOrder;
              validFilterGroup.filters.push(validFilter);
              validFilter.options = [option];
            } else {
              validFilter.options.push(option);
            }
          }
        });
      });
    });
    filterGroups = validFilters;
    if (filterUpFront) {
      let filterCount = -1;
      let filterActiveTab = false;
      filterGroups.forEach((group) => {
        group.filters.forEach((filter) => {
          if (!filterActiveTab && filter.options.some(o => o.checked)) {
            this.filterTab = filterCount;
            filterActiveTab = true;
          }
          filterCount++;
        });
      });
      if (this.elfaFilterType !== 'search') {
        this.filterProducts();
      } else {
        this.setSearchFilters();
      }
      this.spinner.hide();
    } else {
      clearTimeout(this.filterInfoTimer);
      this.noFiltered = this.unFilteredProducts.length;
      this.filtering = true;
      this.filterInfoTimer = setTimeout(() => { this.filtering = false }, environment.filterTimer);
      this.spinner.hide();
    }
  }

  private BrandImage(brandName: string): string {
    if (this.menu) {
      const brandsNode = this.menu?.items?.find(s => s.id == 'man-0');
      if (brandsNode) {
        const brandNode = brandsNode.children.find(n => n.name == brandName);
        if (brandNode) {
          return brandNode.imageUrl;
        }
      }
    }
    return '';
  }

  public changeSort(option: CatalogSortOption): void {
    this.filtersClosed = true;
    this.selectedSort = option;
    this.filterProducts();
  }

  public showFilters(filterType: string): void {
    if (!this.filtersClosed && this.filterType !== filterType) {
      this.filterType = filterType;
      return;
    }
    // Show/Hide the filter Pane
    this.filterType = filterType;
    this.filtersClosed = !this.filtersClosed;
  }

  public filterName(filter: ProductFilter): string {
    if (!filter?.options || filter?.options?.length < 1) {
      return 'No Filters Available';
    }
    if (filter.noSelected) {
      return `${filter.name} (${filter.noSelected})`;
    }
    return filter.name;
  }

  public filterAny(group: ProductFilterGroup): boolean {
    return group.filters.some(f => f.options.some(o => o.checked));
  }

  public filtersAvailable(): boolean {
    var available = false;
    this.filterGroups.forEach((group) => {
      group.filters.forEach((filter) => {
        if (filter?.options?.length > 1) {
          available = true;
        }
      })
    });
    return available;
  }

  public filterRemoveAll(): void {
    this.filtersClosed = true;
    this.filterTab = 0;
    this.filterGroups.forEach((group) => {
      group.filters.forEach((filter) => {
        filter.options.forEach((option) => {
          option.checked = false;
        })
      })
    });
    if (this.elfaFilterType !== 'search') {
      this.filterProducts();
    } else {
      this.setSearchFilters();
      this.searchFilters.emit(this.selectedOptions());
    }
  }

  public filterAllComplete(filter: ProductFilter): boolean {
    return !(filter.options.some(o => !o.checked));
  }

  public filterSomeComplete(filter: ProductFilter): boolean {
    return filter.options.some(o => o.checked) && filter.options.some(o => !o.checked);
  }

  public filterSetAll(checked: boolean, filter: ProductFilter): void {
    filter.options.forEach((option) => {
      option.checked = checked;
    });
    if (this.elfaFilterType !== 'search') {
      this.filterProducts();
    } else {
      this.setSearchFilters();
      this.searchFilters.emit(this.selectedOptions());
    }
  }

  public filterUpdateOption(option: ProductFilterOption): void {
    this.spinner.show();
    option.checked = !option.checked;
    // If we are changing a Brand or Category remove all series selections ... all to do with specific ELFA UX
    if (option.specificationAttributeName.toLowerCase() !== 'series') {
      this.filterGroups.forEach((group) => {
        group.filters.forEach(filter => {
          if (filter.name.toLowerCase() === 'series') {
            filter.options.forEach(option => {
              option.checked = false;
            });
          }
        });
      });
    }
    // According to the Type we need to clear other selections (Cats-> Sub or Brand -> SubCats)
    if (option.specificationAttributeName.toLowerCase() === this.elfaFilterType) {
      let checkedSubCats: ProductFilterOption[] = [];
      this.filterGroups.forEach((group) => {
        group.filters.forEach((filter) => {
          if (filter.name.toLowerCase() === 'subcategory' && filter.options?.some(o => o.checked)) {
            checkedSubCats = filter.options.filter(o => o.checked);
          }
        });
      });
      if (checkedSubCats?.length > 0) {
        let hasProducts = true;
        let checkedFilterType: ProductFilterOption[] = [];
        this.filterGroups.forEach((group) => {
          group.filters.forEach((filter) => {
            if (filter.name.toLowerCase() === this.elfaFilterType && filter.options?.some(o => o.checked)) {
              checkedFilterType = filter.options.filter(o => o.checked);
            }
          });
        });
        if (checkedFilterType?.length > 0) {
          // We have the checked Brands and SubCats ... do we have a combination with no products?
          hasProducts = false;
          this.unFilteredProducts.forEach((product) => {
            if (this.isIncluded(product, false)) {
              hasProducts = true;
            }
          });
        }
        if (!hasProducts) {
          this.filterGroups.forEach((group) => {
            group.filters.forEach((filter) => {
              if (filter.name.toLowerCase() === 'subcategory') {
                filter.options.forEach((option) => {
                  option.checked = false;
                });
              }
            });
          });
        }
      }
    }
    // if we have checked a SubCategory then ensure the Brand is checked to match
    if (option.checked && option.specificationAttributeName.toLowerCase() === 'subcategory') {
      this.filterGroups.forEach((group) => {
        group.filters.forEach((filter) => {
          if (filter.name.toLowerCase() === this.elfaFilterType && filter.options?.some(o => o.checked)) {
            let filterTypesToCheck: string[] = [];
            this.unFilteredProducts.forEach((product) => {
              const subCatSpec = product.specifications?.find(s => s.specificationAttributeName.toLowerCase() === 'subcategory');
              if (subCatSpec && subCatSpec.valueRaw === option.name) {
                product.specifications.filter(s => s.specificationAttributeName.toLowerCase() === this.elfaFilterType).forEach((brand) => {
                  filterTypesToCheck.push(brand.valueRaw);
                });
              }
            });
            filter.options.forEach((option) => {
              if (!option.checked && filterTypesToCheck.some(b => b === option.name)) {
                option.checked = true;
              }
            });
          }
        });
      });
    }
    if (this.elfaFilterType !== 'search') {
      this.filterProducts();
    } else {
      this.setSearchFilters();
      this.searchFilters.emit(this.selectedOptions());
    }
    this.spinner.hide();
  }

  public setSearchFilters() {
    this.filtered = false;
    this.filtering = true;
    clearTimeout(this.filterInfoTimer);
    this.filterInfoTimer = setTimeout(() => { this.filtering = false }, environment.filterTimer);
    this.noFilters = 0;
    this.noFiltered = 0;
    this.filterGroups.forEach((group) => {
      group.filters.forEach((filter) => {
        filter.noSelected = 0;
        const selectedOptions = filter.options.filter(o => o.checked);
        if (selectedOptions && selectedOptions.length > 0) {
          filter.noSelected = selectedOptions.length;
          this.noFilters += selectedOptions.length;
        }
      });
    });
    this.seName = 'search';
    this.setPageSateFiltered(0, 0, 0);
  }

  public filterProducts() {
    if (this.unFilteredProducts.length > environment.pageSize) {
      this.spinner.show();
    }
    this.filtered = false;
    this.filtering = true;
    clearTimeout(this.filterInfoTimer);
    this.filterInfoTimer = setTimeout(() => { this.filtering = false }, environment.filterTimer);
    const filteredPages: ProductsPaged[] = [];
    let filteredProducts: Product[] = [];
    this.unFilteredProducts.forEach((product) => {
      filteredProducts.push(product);
    });
    switch (this.selectedSort?.field) {
      case "name":
        filteredProducts = filteredProducts.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
        if (this.selectedSort.reverse) {
          filteredProducts = filteredProducts.reverse();
        }
        break;
      case "newPrice":
        filteredProducts = filteredProducts.sort((a, b) => (a.newPrice > b.newPrice) ? 1 : ((b.newPrice > a.newPrice) ? -1 : 0));
        if (this.selectedSort.reverse) {
          filteredProducts = filteredProducts.reverse();
        }
        break;
      case "createdOn":
        filteredProducts = filteredProducts.sort((a, b) => (a.createdOn > b.createdOn) ? 1 : ((b.createdOn > a.createdOn) ? -1 : 0));
        break;
      case "position":
      default:
        filteredProducts = filteredProducts.sort((a, b) => (a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0));
        break;
    }
    let pageOfProducts: Product[] = [];
    let index = 0;
    this.noFilters = 0;
    this.filterGroups.forEach((group) => {
      group.filters.forEach((filter) => {
        filter.noSelected = 0;
        const selectedOptions = filter.options.filter(o => o.checked);
        if (selectedOptions && selectedOptions.length > 0) {
          filter.noSelected = selectedOptions.length;
          this.noFilters += selectedOptions.length;
        }
      });
    });
    this.noFiltered = 0;
    let filteredProductsNoSeries: Product[] = [];
    filteredProducts.forEach((product) => {
      if (this.isIncluded(product, true)) {
        this.noFiltered++;
        if (pageOfProducts.length < environment.pageSize) {
          pageOfProducts.push(product);
        } else {
          const page = new ProductsPaged();
          page.pageSize = environment.pageSize;
          page.pageIndex = index;
          page.lastPage = false;
          page.products = [];
          pageOfProducts.forEach((productToAdd) => {
            page.products.push(productToAdd);
          })
          filteredPages.push(page)
          index++;
          pageOfProducts = [];
          pageOfProducts.push(product);
        }
      }
      if (this.isIncluded(product, false)) {
        filteredProductsNoSeries.push(product);
      };
    });
    const page = new ProductsPaged();
    page.pageSize = environment.pageSize;
    page.pageIndex = index;
    page.lastPage = true;
    page.products = [];
    pageOfProducts.forEach((productToAdd) => {
      page.products.push(productToAdd);
    })
    filteredPages.push(page);
    if (filteredPages?.length > 0) {
      filteredPages[0].visible = true;
    }
    filteredProducts = []; // Gives us back the Filtered Products to use if we want to
    filteredPages.forEach(page => {
      page.products.forEach(product => {
        filteredProducts.push(product);
      });
    });
    this.productsPagedService.replace(filteredPages);
    this.setPageSateFiltered(0, 0, 0);
    if (this.unFilteredProducts.length > environment.pageSize) {
      this.spinner.hide();
    }
    // Finally set the filter status, disabling those which do not now have any products (Series Only)
    this.filterGroups.forEach((group) => {
      let catFilters = false;
      group.filters.forEach(filter => {
        if (!catFilters && filter.name.toLowerCase() !== 'series') {
          catFilters = filter.noSelected > 0;
        }
        if (filter.name.toLowerCase() === 'series') { // Series filter - Only show relevant Series values
          filter.options.forEach(option => {
            if (catFilters) {
              option.disabled = !filteredProductsNoSeries.some(p => p.specifications?.find(s => s.specificationAttributeName.toLowerCase() === 'series')?.valueRaw === option.name);
            } else {
              option.disabled = false;
            }
          });
        }
      });
    });
  }

  public setPageSateFiltered(pageNumber: number, position: number, scrollY: number): void {
    let selectedOptions: ProductFilterOption[] = [];
    this.filterGroups?.forEach((group) => {
      group.filters.forEach((filter) => {
        filter.options.forEach((option) => {
          if (option.checked) {
            selectedOptions.push(option);
          }
        })
      })
    });
    localStorage.setItem(this.seName, JSON.stringify(new PageState(pageNumber, position, scrollY, this.selectedSort, selectedOptions)));
  }

  public selectedOptions(): ProductFilterOption[] {
    let selectedOptions: ProductFilterOption[] = [];
    this.filterGroups?.forEach((group) => {
      group.filters.forEach((filter) => {
        filter.options.forEach((option) => {
          if (option.checked) {
            selectedOptions.push(option);
          }
        })
      })
    });
    return selectedOptions;
  }

  public isIncluded(product: Product, withSeries: boolean): boolean {
    let included = true;
    this.filterGroups.forEach((group) => {
      group.filters.forEach((filter) => {
        const selectedOptions = filter.options.filter(o => o.checked);
        if (selectedOptions && selectedOptions.length > 0) {
          this.filtered = true;
          if (product.specifications && included) {
            let matches: ProductFilterOption[] = [];
            product.specifications.forEach((spec) => {
              matches = matches.concat(selectedOptions.filter(o => o.specificationAttributeId === spec.specificationAttributeId && o.name === spec.valueRaw));
            });
            if (!withSeries) {
              product.specifications.forEach((spec) => {
                matches = matches.concat(selectedOptions.filter(o => spec.specificationAttributeName.toLowerCase() === 'series' && o.specificationAttributeId === spec.specificationAttributeId));
              });
            }
            if (matches.length === 0) {
              included = false;
            }
          }
        }
      })
    });
    return included;
  }

  public compareSortOptions(option1: CatalogSortOption, option2: CatalogSortOption): boolean {
    if (!option1 || !option2) {
      return false;
    }
    return option1.id === option2.id;
  }
}
